/**
 * Блок «Пост» карточки изображения: текст на продажу и кадр под соцсеть.
 *
 * Текст собирается из `GET …/post` (артикул CRM и цена «от») по шаблону `lib/post.ts`,
 * правится руками и копируется; не сохраняется. «Арт.» в тексте — артикул CRM (общий у
 * всех экземпляров, побеждает введённую при привязке серию); привязано другой серией —
 * подпись «привязано по серии N» под строкой модели. Платье без привязки — поле «Серия»
 * (`CrmSeries`) над текстом; платья нет — поле серии только для текста. CRM недоступна —
 * предупреждение, показывается сохранённая серия, ввод руками работает.
 *
 * Кадр: «как есть | 4:5 | 1:1 | 9:16», рамка двигается мышью/пальцем по превью;
 * «Скачать» режет оригинал (`/media` — тот же origin) в canvas на белом фоне (прозрачный PNG не
 * чернеет в JPEG), JPEG q=0.92; площадь больше `EXPORT_MAX_PX` (предел холста iOS Safari) —
 * выход пропорционально ужимается. Без сервера.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Copy, Download } from "lucide-react";
import { useRef, useState, type PointerEvent } from "react";
import { Link, useNavigate } from "react-router";

import { CrmSeries } from "@/components/photos/CrmSeries";
import { btn, btnGhost, ErrorLine, input, Segmented } from "@/components/trends/ui";
import { copyText, downloadBlob } from "@/lib/exportList";
import { mediaUrl } from "@/lib/media";
import {
  ASPECTS,
  boundSeriesNote,
  cropFileName,
  cropRect,
  EXPORT_MAX_PX,
  exportScale,
  modelLabel,
  postText,
  type Aspect,
} from "@/lib/post";
import { ApiFailure } from "@/lib/api";
import { entryPlan } from "@/lib/postEntry";
import type { PostCandidate } from "@/lib/postPlan";
import { createPlan, loadCandidates, postsKeys } from "@/lib/postsApi";
import { loadPostData, resultKeys } from "@/lib/results";

export function PostPanel({ variantId, assetPath }: { variantId: number; assetPath: string }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: resultKeys.post(variantId), queryFn: () => loadPostData(variantId), retry: 1 });
  // серия руками — у варианта без платья (живёт только в тексте этого поста)
  const [manualSeries, setManualSeries] = useState("");
  // null — текст по шаблону; правка руками его фиксирует
  const [edited, setEdited] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean | null>(null);

  const d = q.data;
  const series = d?.series ?? (manualSeries.trim() || null);
  const text = edited ?? postText(series, d?.price_from ?? null);
  // «Арт.» в тексте — артикул CRM; привязано другой серией — подпись под ним
  const boundNote = d ? boundSeriesNote(d.series, d.bound_series) : null;

  const copy = async () => {
    const ok = await copyText(text);
    setCopied(ok);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="grid gap-3" data-annot="пост">
      {q.isError ? <ErrorLine error={q.error} /> : null}
      {d?.crm_error ? (
        <span className="w-fit rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn">
          {d.crm_error} — серию и цену впишите руками
        </span>
      ) : null}
      {d && d.dress_id !== null && d.crm_model_id === null ? (
        <div className="grid gap-1">
          <span className="text-[11.5px] text-fg-faint">Платье не привязано к CRM</span>
          <CrmSeries
            dressId={d.dress_id}
            onBound={() => client.invalidateQueries({ queryKey: resultKeys.post(variantId) })}
          />
        </div>
      ) : null}
      {d && d.dress_id === null ? (
        <label className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-fg-faint">Серия</span>
          <input
            className={clsx(input, "w-36")}
            placeholder="только для текста"
            maxLength={20}
            value={manualSeries}
            onChange={(e) => setManualSeries(e.target.value)}
          />
        </label>
      ) : null}
      {d && d.crm_model_id !== null && !d.crm_error ? (
        <span className="text-[12.5px] text-fg-muted">
          {modelLabel(d.model_name) ?? `модель #${d.crm_model_id}`}
          {d.in_stock ? null : <span className="text-warn"> · в салонах нет в наличии</span>}
        </span>
      ) : null}
      {boundNote ? <span className="text-[11.5px] text-fg-faint">{boundNote}</span> : null}

      <div className="grid gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] text-fg-faint">Текст</span>
          <span className="flex items-center gap-1">
            {edited !== null ? (
              <button type="button" className={btnGhost} onClick={() => setEdited(null)} title="Собрать заново по шаблону">
                по шаблону
              </button>
            ) : null}
            <button type="button" className={btnGhost} onClick={copy}>
              <Copy size={12} />
              {copied === null ? "Копировать" : copied ? "Скопировано" : "Не скопировалось"}
            </button>
          </span>
        </div>
        <textarea
          aria-label="Текст поста"
          className={clsx(input, "h-auto min-h-[76px] py-1.5 leading-snug")}
          rows={3}
          value={text}
          onChange={(e) => setEdited(e.target.value)}
        />
      </div>

      <PostEntry variantId={variantId} />
      <Frame variantId={variantId} assetPath={assetPath} series={series} />
    </div>
  );
}

/**
 * Вход в модерацию публикации: «Открыть публикацию N» (есть не отменённая) или «Подготовить публикацию»
 * (создаёт и открывает `/posts/:id`). `plans` дан сеткой «В публикацию» (одна выборка на всю сетку) —
 * тогда своего запроса нет. Видео сюда не передают.
 */
export function PostEntry({ variantId, plans }: { variantId: number; plans?: PostCandidate["plans"] | undefined }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const own = useQuery({
    queryKey: postsKeys.candidates(variantId),
    queryFn: () => loadCandidates(variantId),
    enabled: plans === undefined,
    retry: 1,
  });
  const found = entryPlan(plans ?? own.data?.find((c) => c.variant_id === variantId)?.plans ?? []);
  const create = useMutation({
    mutationFn: () => createPlan(variantId),
    onSuccess: async (plan) => {
      await client.invalidateQueries({ queryKey: postsKeys.all });
      navigate(`/posts/${plan.id}`);
    },
  });
  const err = create.error;
  const message = err instanceof ApiFailure && err.code === "NOT_PUBLISH" ? "сначала отметьте фото «В публикацию»" : null;

  // список публикаций варианта неизвестен (грузится / ошибка) — создавать нельзя: будет дубль
  const known = plans !== undefined || own.isSuccess;

  return (
    <div className="grid gap-1" data-annot="публикация">
      {plans === undefined && own.isError ? <ErrorLine error={own.error} /> : null}
      {!known ? null : found ? (
        <Link to={`/posts/${found.id}`} className={`${btn} w-fit`}>
          Открыть публикацию {found.id}
        </Link>
      ) : (
        <button
          type="button"
          className={`${btn} w-fit`}
          disabled={create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Готовлю…" : "Подготовить публикацию"}
        </button>
      )}
      {message ? <span className="text-[12px] text-warn">{message}</span> : <ErrorLine error={err} />}
    </div>
  );
}

/** Размер выхода рамки `w×h`: не больше `EXPORT_MAX_PX` по площади, пропорция та же. */
function outSize(w: number, h: number): { w: number; h: number } {
  const s = exportScale(w, h, EXPORT_MAX_PX);
  return { w: Math.max(1, Math.floor(w * s)), h: Math.max(1, Math.floor(h * s)) };
}

/** Кадр под соцсеть: сегмент пропорции, превью с рамкой, скачивание обрезки из оригинала. */
function Frame({ variantId, assetPath, series }: { variantId: number; assetPath: string; series: string | null }) {
  const [aspect, setAspect] = useState<Aspect>("4:5");
  const [center, setCenter] = useState({ cx: 0.5, cy: 0.5 });
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const src = mediaUrl(assetPath);

  const r = size ? cropRect(size.w, size.h, aspect, center.cx, center.cy) : null;
  const out = r ? outSize(r.w, r.h) : null;
  // рамка в долях картинки: превью любого размера, геометрия — оригинала
  const frameStyle =
    size && r
      ? {
          left: `${(r.x / size.w) * 100}%`,
          top: `${(r.y / size.h) * 100}%`,
          width: `${(r.w / size.w) * 100}%`,
          height: `${(r.h / size.h) * 100}%`,
        }
      : undefined;

  const moveTo = (e: PointerEvent<HTMLDivElement>) => {
    const b = box.current?.getBoundingClientRect();
    if (!b || !b.width || !b.height) return;
    const clamp01 = (v: number) => Math.min(Math.max(v, 0), 1);
    setCenter({ cx: clamp01((e.clientX - b.left) / b.width), cy: clamp01((e.clientY - b.top) / b.height) });
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const img = new Image();
      img.src = src;
      await img.decode();
      const rect = cropRect(img.naturalWidth, img.naturalHeight, aspect, center.cx, center.cy);
      const o = outSize(rect.w, rect.h);
      const canvas = document.createElement("canvas");
      canvas.width = o.w;
      canvas.height = o.h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("браузер не дал canvas");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, o.w, o.h);
      ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, o.w, o.h);
      const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", 0.92));
      if (!blob) throw new Error("не удалось собрать JPEG");
      downloadBlob(blob, cropFileName(series, variantId, aspect));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-2" data-annot="кадр поста">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented<Aspect> label="Кадр" options={ASPECTS} value={aspect} onPick={setAspect} />
        <button type="button" className={btn} disabled={busy || !size} onClick={download}>
          <Download size={12} />
          {busy ? "Готовлю…" : "Скачать"}
        </button>
        {out ? (
          <span className="tnum text-[11.5px] text-fg-faint">
            {out.w}×{out.h}
          </span>
        ) : null}
      </div>
      <div
        ref={box}
        className={clsx(
          "relative w-full max-w-[280px] touch-none select-none overflow-hidden rounded-[4px] border border-border",
          aspect !== "orig" && "cursor-move",
        )}
        onPointerDown={(e) => {
          if (aspect === "orig") return;
          e.currentTarget.setPointerCapture(e.pointerId);
          moveTo(e);
        }}
        onPointerMove={(e) => {
          if (aspect !== "orig" && e.currentTarget.hasPointerCapture(e.pointerId)) moveTo(e);
        }}
        title={aspect === "orig" ? undefined : "Перетащите рамку"}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="block w-full"
          onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
        {aspect !== "orig" && frameStyle ? (
          <div
            className="pointer-events-none absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
            style={frameStyle}
          />
        ) : null}
      </div>
      <ErrorLine error={error} />
    </div>
  );
}
